import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ClipboardList,
  BarChart3,
  ArrowLeft,
  LogOut,
  User,
  Settings,
  ChevronDown,
  Activity,
  LayoutGrid,
  Loader2,
  UserPlus,
  TrendingUp,
  PieChart,
  Target,
  Users,
  FileText,
  Download
} from 'lucide-react';
import { authAPI, surveysAPI } from '../services/api';

const SurveyAnalyticsPage = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [surveyId]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      const surveyData = await surveysAPI.getById(surveyId);
      setSurvey(surveyData);
      
      const analyticsData = await surveysAPI.getAnalytics(surveyId);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
      if (error.response?.status === 401) navigate('/auth');
      if (error.response?.status === 404) navigate('/surveys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/auth');
  };

  const handleExportReport = async () => {
    try {
      const blob = await surveysAPI.exportReport(surveyId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey_report_${survey.title.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to export report');
    }
  };

  const isAdmin = user?.role === 'administrator';

  const getTypeColor = (type) => {
    const colors = {
      kap: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      mrs: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      patient: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      hcp: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
    };
    return colors[type] || 'bg-slate-500/10 text-slate-400';
  };

  const getTypeName = (type) => {
    const names = { kap: 'KAP Survey', mrs: 'Market Research', patient: 'Patient Survey', hcp: 'HCP Feedback' };
    return names[type] || type;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      administrator: 'bg-amber-500/10 text-amber-400',
      doctor: 'bg-blue-500/10 text-blue-400',
      agency: 'bg-purple-500/10 text-purple-400',
      sponsor: 'bg-emerald-500/10 text-emerald-400'
    };
    return colors[role] || 'bg-slate-500/10 text-slate-400';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 z-40 hidden lg:flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <LayoutGrid className="w-5 h-5" /> Dashboard
          </button>
          <button onClick={() => navigate('/surveys')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <ClipboardList className="w-5 h-5" /> Surveys
          </button>
          <button onClick={() => navigate('/analytics')} className="w-full flex items-center gap-3 px-4 py-3 text-white bg-white/10 rounded-xl font-medium">
            <BarChart3 className="w-5 h-5" /> Analytics
          </button>
          {isAdmin && (
            <button onClick={() => navigate('/users')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
              <UserPlus className="w-5 h-5" /> Manage Users
            </button>
          )}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{isAdmin ? 'Administrator' : user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300"><User className="w-4 h-4" /> Profile</DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-slate-300"><Settings className="w-4 h-4" /> Settings</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}><LogOut className="w-4 h-4" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/analytics')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white">{survey?.title}</h1>
                <Badge variant="outline" className={getTypeColor(survey?.type)}>{getTypeName(survey?.type)}</Badge>
              </div>
              <p className="text-slate-400">Survey Analytics & Insights</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate(`/surveys/${surveyId}/builder`)} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <FileText className="w-4 h-4 mr-2" /> View Survey
            </Button>
            <Button onClick={handleExportReport} className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500">
              <Download className="w-4 h-4 mr-2" /> Export Report
            </Button>
          </div>
        </div>

        {analytics && (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Responses</p>
                      <p className="text-3xl font-bold text-white mt-1">{analytics.total_responses}</p>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Target Responses</p>
                      <p className="text-3xl font-bold text-white mt-1">{analytics.target_responses}</p>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                      <Target className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Completion Rate</p>
                      <p className="text-3xl font-bold text-cyan-400 mt-1">{analytics.completion_rate}%</p>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-cyan-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Assigned Users</p>
                      <p className="text-3xl font-bold text-white mt-1">{analytics.assigned_users_count}</p>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                      <UserPlus className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Progress Bar */}
            <Card className="bg-slate-900 border-slate-800 mb-8">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Response Progress</h3>
                  <span className="text-slate-400">{analytics.total_responses} / {analytics.target_responses}</span>
                </div>
                <div className="h-4 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all"
                    style={{ width: `${Math.min(100, analytics.completion_rate)}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-cyan-400" /> Users by Role
                  </CardTitle>
                  <CardDescription className="text-slate-400">Distribution of assigned users</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.keys(analytics.users_by_role).length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No users assigned</p>
                    ) : (
                      Object.entries(analytics.users_by_role).map(([role, count]) => {
                        const total = analytics.assigned_users_count;
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={role}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-300 capitalize">{role}</span>
                              <span className="text-slate-400">{count} ({percentage}%)</span>
                            </div>
                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                              <div className={`h-full ${getRoleBadgeColor(role).includes('emerald') ? 'bg-emerald-500' : getRoleBadgeColor(role).includes('blue') ? 'bg-blue-500' : getRoleBadgeColor(role).includes('purple') ? 'bg-purple-500' : 'bg-amber-500'} rounded-full`} style={{ width: `${percentage}%` }} />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-cyan-400" /> Questions Overview
                  </CardTitle>
                  <CardDescription className="text-slate-400">{analytics.questions_count} total questions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {analytics.question_stats.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No questions added</p>
                    ) : (
                      analytics.question_stats.map((q, i) => (
                        <div key={q.id || i} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                          <span className="text-slate-300 truncate flex-1">Q{i + 1}: {q.text || '(No text)'}</span>
                          <div className="flex items-center gap-2 ml-2">
                            <Badge className="bg-slate-700 text-slate-300">{q.type}</Badge>
                            {q.required && <Badge className="bg-red-500/10 text-red-400">Required</Badge>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Survey Description */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Survey Details</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 mb-4">{survey?.description || 'No description provided'}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge variant="outline" className={survey?.status === 'active' ? 'text-emerald-400 border-emerald-500/20' : 'text-slate-400 border-slate-600'}>
                      {survey?.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Created</p>
                    <p className="text-white">{new Date(survey?.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Last Updated</p>
                    <p className="text-white">{new Date(survey?.updated_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Created By</p>
                    <p className="text-white">{survey?.created_by}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default SurveyAnalyticsPage;
